import React, { useState } from 'react';
import { useTranslator } from 'tradera/lang/translate';
import { useToggleEmailNotificationMutation } from 'tradera/apps/my-tradera/saved-searches/saved-searches-api';
import CloseIcon from '@tradera/blueprint/theme/icons2/close.svg?jsx';
import cn from 'classnames';
import * as searchGtm from 'tradera/apps/tradera/components/discover/helpers/search-gtm';
import { showErrorToast } from 'tradera/services/toasts';
import styles from './email-notification-tooltip.module.scss';

export enum EmailNotificationTooltipPosition {
    Top = 'top',
    Bottom = 'bottom',
    Left = 'left'
}

type EmailNotificationToolTipProps = {
    searchName: string;
    searchId: number;
    isSavedSearchEmailEnabled: boolean;
    onClose: () => void;
    center?: boolean;
    position?: EmailNotificationTooltipPosition;
};

export const EmailNotificationToolTip = ({
    searchName,
    searchId,
    isSavedSearchEmailEnabled,
    onClose,
    center,
    position = EmailNotificationTooltipPosition.Bottom
}: EmailNotificationToolTipProps) => {
    const { t } = useTranslator();
    const [isEmailChecked, setIsEmailChecked] = useState(true);
    const [toggleEmailNotification] = useToggleEmailNotificationMutation();

    const handleEmailToggle = () => {
        setIsEmailChecked((isEmailEnabled) => !isEmailEnabled);
        toggleEmailNotification({
            savedSearchDto: { id: searchId, sendMail: !isEmailChecked }
        })
            .unwrap()
            .catch(() => {
                setIsEmailChecked((isEmailEnabled) => !isEmailEnabled);
                showErrorToast('searchResults_saveSearch_error');
            });
        searchGtm.trackAction(
            searchGtm.GA_EVENT_SAVE_SEARCH,
            isEmailChecked ? 'Email removed' : 'Email added'
        );
    };

    const boxClass = cn(styles.emailNotificationBox, {
        [styles.emailNotificationBoxCenter]: center,
        [styles.emailNotificationBoxLeft]:
            position === EmailNotificationTooltipPosition.Left,
        [styles.emailNotificationBoxTop]:
            position === EmailNotificationTooltipPosition.Top
    });

    return (
        <div className={boxClass}>
            <span className="d-block text-inter-medium mr-2">
                {t('searchResults_saveSearch_savedTooltip', {
                    searchName
                })}
            </span>
            {isSavedSearchEmailEnabled ? (
                <span className="form-check form-check-sm form-check-white my-0 mt-1 d-flex justify-content-between">
                    <label
                        htmlFor="mail-notification"
                        className="form-toggle-label my-0 text-nowrap cursor-pointer">
                        {t('searchResults_saveSearch_savedTooltipNotify')}
                    </label>
                    <input
                        type="checkbox"
                        id="mail-notification"
                        className={`form-toggle-input ${styles.emailNotificationToggleInput}`}
                        name="mail-notification"
                        onChange={() => {
                            handleEmailToggle();
                        }}
                        checked={isEmailChecked}
                    />
                </span>
            ) : (
                <p className="mt-1">
                    {t('searchResults_saveSearch_emailDisabledTooltip')}
                </p>
            )}
            <button
                className={`unbutton ${styles.emailNotificationBoxClose}`}
                data-test-save-search-tooltip
                onClick={() => onClose()}>
                <CloseIcon className="icon icon-xs" />
            </button>
        </div>
    );
};
